import { useMemo, useState } from "react";

import { Lightbox } from "react-modal-image";

const baixarComoArquivo = async (url, nomeSugerido) => {
  if (!url) return;
  try {
    const res = await fetch(url, { credentials: "omit", mode: "cors" }).catch(
      () => null,
    );
    let href = url;
    let nomeArquivo = nomeSugerido || "figurinha";
    let blob = null;
    if (res && res.ok) {
      blob = await res.blob().catch(() => null);
      if (blob) {
        href = URL.createObjectURL(blob);
        const cd = res.headers.get("content-disposition");
        if (cd) {
          const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (m && m[1]) nomeArquivo = m[1].replace(/['"]/g, "");
        } else {
          try {
            const u = new URL(url, window.location.href);
            const ultimo = u.pathname.split("/").filter(Boolean).pop();
            if (ultimo) nomeArquivo = ultimo;
          } catch {
            /* usa o sugerido */
          }
        }
      }
    }
    const a = document.createElement("a");
    a.href = href;
    a.download = nomeArquivo;
    a.rel = "noopener noreferrer";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
    if (blob && href.startsWith("blob:")) {
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    }
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

const normalizarParaMesmaOrigem = (url) => {
	if (!url) return "";
	const s = String(url);
	if (/^https?:\/\//i.test(s)) {
		try {
			const u = new URL(s);
			const hostAtual =
				typeof window !== "undefined" ? window.location.hostname : "";
			const ehLocal = (h) =>
				h === "localhost" ||
				h === "127.0.0.1" ||
				h === "0.0.0.0" ||
				h === "";
			const ehNosso =
				u.hostname === hostAtual || ehLocal(u.hostname);
			if (ehNosso) {
				return u.pathname + u.search + u.hash;
			}
		} catch {
			/* no-op: URL malformada, retorna original sem normalizar */
		}
	}
	return s;
};

export const miniaturaDe = (url) => {
	if (!url) return url;
	const normalizada = normalizarParaMesmaOrigem(url);
	if (normalizada.includes("/public/")) {
		return normalizada.replace("/public/", "/miniatura/");
	}
	if (/^\/public\//i.test(normalizada)) {
		return normalizada.replace(/^\/public\//i, "/miniatura/");
	}
	const s = String(url);
	if (/^https?:\/\//i.test(s) && s.includes("/public/")) {
		return s.replace("/public/", "/miniatura/");
	}
	return s;
};

const urlOriginal = (imageUrl) => normalizarParaMesmaOrigem(imageUrl);

const ModalImageCors = ({ imageUrl, stickerMode }) => {
	const [aberta, setAberta] = useState(false);
	const [erroMiniatura, setErroMiniatura] = useState(false);
	const [erroOriginal, setErroOriginal] = useState(false);
	const [carregada, setCarregada] = useState(false);

	if (!imageUrl) return null;

  // ── Figurinha animada / salvar (ROOT CAUSES dos 2 bugs relatados) ──
  //
  // BUG 1 (figurinha animada parada): miniaturaDe() gera /miniatura/...
  //  que é uma IMAGEM ESTÁTICA do servidor (sharp redimensiona e remove
  //  quadros > 1 de WebP/GIF/apng). Para stickerMode, a miniatura NÃO DEVE
  //  ser usada nunca — tem que carregar a ORIGINAL direto (loop e play são
  //  automáticos no navegador para <img> de GIF/WebP animado).
  //
  // BUG 2 (salvar impossível porque clique abre modal): <img> dentro de
  //  <div onClick={() => setAberta(true)}> captura CLIQUES DIREITOS no
  //  bubble e algumas vezes impede o menu de contexto de "salvar imagem
  //  como". Corrigimos em 2 frentes:
  //    (a) onMouseDown/onClick no wrapper checam `button` — 0 (esquerdo)
  //        SEM modifiers abre o modal. Botão direito (2), Shift, Ctrl e
  //        clique com tecla de contexto NÃO abrem mais o modal.
  //    (b) onContextMenu não tem preventDefault() → navegador mostra menu
  //        nativo em cima do <img> com a opção de salvar.
  //    (c) <img> ganha draggable=true e user-select auto (podem arrastar
  //        ou usar plugins de extensão para baixar).
  //
  // Como recompensa (UI melhor), Lightbox ganha barra inferior com botões
  // BAIXAR e ABRIR EM NOVA ABA.
  //
  // Só cai em miniaturaDe SE (a) não for stickerMode E (b) não deu erro
  // antes. Para stickerMode, sempre ORIGINAL.
  const forcarOriginal = Boolean(stickerMode);
  const srcUsar = useMemo(() => {
    if (forcarOriginal) return urlOriginal(imageUrl);
    if (erroMiniatura) return urlOriginal(imageUrl);
    return miniaturaDe(imageUrl);
  }, [forcarOriginal, imageUrl, erroMiniatura]);

  // Garante <img> sempre recarregar srcUsar se trocar — evita cachear a
  // miniatura estática quando alternamos stickerMode em render rápido.
  const imgKey = useMemo(
    () => `${forcarOriginal ? "orig" : "thumb"}::${srcUsar}`,
    [forcarOriginal, srcUsar],
  );

	const errorFinal = forcarOriginal ? erroOriginal : erroMiniatura && erroOriginal;

	const miniaturaClasses = stickerMode
		? "h-[130px] w-[130px] object-contain"
		: "h-[200px] w-[250px] rounded-lg object-cover";
	const miniaturaEsqueletoClasses = stickerMode
		? "h-[130px] w-[130px] rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground"
		: "h-[200px] w-[250px] rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20 p-3 text-xs text-muted-foreground";
	const esqueletoPulseClasses = stickerMode
		? "absolute inset-0 animate-pulse rounded-2xl bg-muted pointer-events-none"
		: "absolute inset-0 animate-pulse rounded-lg bg-muted pointer-events-none";
	const titulo = stickerMode ? "Clique esquerdo para ampliar · Clique direito para salvar" : "Abrir pré-visualização";
	const rotulo = stickerMode ? "Figurinha" : "Imagem enviada na conversa";
	const rotuloErro = stickerMode ? "Figurinha não carregada" : "Imagem";
	const subTituloErro = stickerMode
		? "Não foi possível carregar a figurinha."
		: "Não foi possível carregar";
	const abrirNovaAbaLabel = stickerMode
		? "Abrir figurinha em nova aba"
		: "Abrir em nova aba";

	if (errorFinal) {
		return (
			<div
				className={`flex ${
					stickerMode
						? "h-[130px] w-[130px] flex-col items-center justify-center gap-1 rounded-2xl"
						: "h-[200px] w-[250px] flex-col items-center justify-center gap-2 rounded-lg"
				} ${miniaturaEsqueletoClasses} flex`}
				title={rotuloErro}
			>
				<span className="text-[11px] uppercase tracking-wider">{rotuloErro}</span>
				{subTituloErro && (
					<span className="text-center leading-snug">{subTituloErro}</span>
				)}
				<a
					href={urlOriginal(imageUrl)}
					target="_blank"
					rel="noreferrer noopener"
					className="underline-offset-2 hover:underline"
				>
					{abrirNovaAbaLabel}
				</a>
			</div>
		);
	}

	const handleErroImg = () => {
		if (forcarOriginal) {
			if (!erroOriginal) setErroOriginal(true);
			return;
		}
		if (!erroMiniatura) {
			setErroMiniatura(true);
			return;
		}
		if (!erroOriginal) {
			setErroOriginal(true);
		}
	};

  // Wrapper NÃO CAPTURA CLIQUES MAIS — deixa 100% para <img> nativo,
  // para menu de contexto (c direito "Salvar imagem como") aparecer sempre.
  // A única forma de abrir o lightbox agora é CLICAR ESQUERDO NA IMAGEM
  // (botão 0 sem modifiers) ou botão overlay "Aumentar" no canto superior
  // direito da miniatura, ou Enter/Space se a miniatura for focada via tab.
  const abrirModalSeguroImg = (e) => {
    if (e.defaultPrevented) return;
    if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
    if (typeof e.button === "number" && e.button !== 0) return;
    e.stopPropagation?.();
    e.preventDefault?.();
    setAberta(true);
  };
  // Se usuário pressiona Enter/Space enquanto foco está no wrapper (por
  // acessibilidade), abre também — mas sem bloquear menu nativo.
  const onKeyDownWrapper = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setAberta(true);
    }
  };

	return (
		<>
			<div
				className="relative inline-block"
				title={titulo}
        tabIndex={0}
        role="group"
        onKeyDown={onKeyDownWrapper}
			>
				{!carregada && (
					<div
						className={esqueletoPulseClasses}
					/>
				)}
				<img
          key={imgKey}
					src={srcUsar}
					alt={rotulo}
					loading={forcarOriginal ? "eager" : erroMiniatura ? "eager" : "lazy"}
					decoding="async"
          draggable
					onLoad={() => setCarregada(true)}
					onError={handleErroImg}
					className={miniaturaClasses}
          style={{ WebkitUserDrag: "auto", userSelect: "auto" }}
          onClick={abrirModalSeguroImg}
          onContextMenu={(e) => {
            // 100% garantia de menu contexto aparecer na figurinha: não
            // propagar este evento para qualquer wrapper ancestral que
            // possa ter onClick/onMouseDown ainda (em navegadores mais
            // antigos, bubble do contextmenu disparava listeners).
            e.stopPropagation();
            // NÃO usar e.preventDefault() — deixa o menu nativo aparecer.
          }}
				/>
			</div>
			{aberta && (
        <div className="relative z-[9999]">
          <Lightbox
            medium={urlOriginal(imageUrl)}
            large={urlOriginal(imageUrl)}
            alt={rotulo}
            onClose={() => setAberta(false)}
            imageBackgroundColor="transparent"
          />
          {/* Barra de ações do Lightbox (top-right do viewport) */}
          <div className="pointer-events-none fixed inset-x-0 top-0 z-[10000] flex justify-end p-3 sm:p-4">
            <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-black/60 p-2 text-xs text-white shadow-2xl backdrop-blur">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                onClick={(e) => {
                  e.stopPropagation();
                  baixarComoArquivo(urlOriginal(imageUrl), stickerMode ? "figurinha" : "imagem");
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                {stickerMode ? "Baixar figurinha" : "Baixar imagem"}
              </button>
              <a
                href={urlOriginal(imageUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 no-underline hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                Abrir em nova aba
              </a>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                onClick={(e) => {
                  e.stopPropagation();
                  setAberta(false);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                Fechar
              </button>
            </div>
          </div>
        </div>
			)}
		</>
	);
};

export default ModalImageCors;
